/* eslint-disable max-params */
import {
  CELL_FILTER,
  IModalDialogOptions,
  WIDGET_COMPONENT,
  CELL_RENDERER,
  TRANSFER_TYPE,
  COMMON_ACTION_TYPES,
  IEvent,
  IPrintOpenSheetOptions,
} from '../../types';
import {
  filter,
  get,
  includes,
  isEmpty,
  isEqual,
  isNil,
  map,
  merge,
  startCase,
} from 'lodash';
import {
  BTN_CANCEL,
  BTN_CLOSE,
  BTN_DONE,
} from '@text-content/button-templates';
import {
  COLUMN_SELECTOR_ID,
  GRID_ROW_SELECTION,
  XUX_GRID_ACTIONS,
} from '@geniuspos/ui-shared';
import { STAND_WORKSHEET_REINITIALIZED_TOOLTIP_MESSAGE } from '@text-content/messages';
import { CellClassParams, ICellRendererParams } from 'ag-grid-community';
import {
  WIDGET_FAMILY,
  TIME_FORMATS,
  EVENT_SELECTOR_SELECT,
} from '@config/global-constants';
import { IViewSettingModalParams } from '@shared/types/entities/stand-worksheet';
import { generateSpacesText, isNeedDMSync, toMoment } from '@shared/utils';
import {
  COMMON_TEXT_GENERAL_MESSAGE_CLASS,
  COMMON_WARN_MESSAGE_CLASS,
} from '@app/shared/constants/common-warn-grid-setting';
import {
  TRANSFER_STATUS_NAME_OPTIONS,
  TRANSFER_STATUS_VALUE,
} from '@app/shared/types/enums/transfer-status.enum';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import { StandWorksheetFormService } from './stand-worksheet.form.service';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import { transferTypeFormatter } from '../transfer/transfer.util';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { Observable } from 'rxjs';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';
import { EVENT_STATUS_TEXT_MAPPING } from '@app/shared/types/enums/event-status.enum';

const eventSelectorModal = (
  selectedEventIds: string[],
  filterType: string[],
  eventList: IEvent[],
): IModalDialogOptions => {
  return merge(
    {},
    {
      title: 'Please Select an Event',
      validation: true,
      modalClass: `modal-lg hidden-header-checkbox`,
      buttons: [
        {
          ...BTN_DONE,
          caption: 'Select Event',
          disabled: (value): boolean => isEmpty(value.events),
        },
        BTN_CANCEL,
      ],
      value: {
        events: selectedEventIds,
      },
      schema: {
        type: 'object',
        properties: {
          events: {
            type: 'array',
          },
        },
      },
      formRules: {
        fields: {
          events: {
            title: null,
            ignoreAutoTitleWhenTranslate: true,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.grid_list,
            widgetData: {
              userGridOptions: {
                rowSelection: GRID_ROW_SELECTION.SINGLE,
              },
              gridHeight: '270px',
              hideActionPanel: true,
              valueField: 'id',
              selectOptions: filter(eventList, (item) =>
                includes(filterType, item.status),
              ),
              columns: [
                {
                  title: 'Event Name',
                  path: 'name',
                  defaultView: true,
                  sortable: false,
                },
                {
                  title: 'Event Date',
                  path: 'date',
                  defaultView: true,
                },
                {
                  title: 'Event Type',
                  path: 'event_type_entity_name',
                  defaultView: true,
                },
                {
                  title: 'Status',
                  path: 'status',
                  defaultView: true,
                  cellRenderer: (record: ICellRendererParams): string =>
                    startCase(record?.value),
                },
              ],
            },
          },
        },
      },
    },
  );
};

const printOpenSheetsModal = ({
  eventList,
  validator,
  i18nService,
}: IPrintOpenSheetOptions): IModalDialogOptions => {
  return merge(
    {},
    {
      showLoader: true,
      title: I18n.Forms.StandWorksheetForm.PrintOpeningSheets,
      validation: true,
      modalClass: `modal-lg hidden-header-checkbox`,
      buttons: [
        {
          ...BTN_DONE,
          caption: I18n.Common.Print,
        },
        BTN_CANCEL,
      ],
      value: {
        chargeable: true,
        event_ids: [],
        message: '',
      },
      schema: {
        type: 'object',
        properties: {
          event_ids: {
            type: 'array',
          },
          chargeable: {
            type: 'boolean',
          },
        },
      },
      formRules: {
        fields: {
          event_ids: {
            title: I18n.EntityNames.Event,
            widgetFamily: WIDGET_FAMILY.XUX,
            required: true,
            selectOptions: map(eventList, (item: IEvent) => ({
              text: generateSpacesText(
                item.name,
                i18nService.instant(
                  get(EVENT_STATUS_TEXT_MAPPING, item.status, item.status),
                ),
              ),
              value: item.id,
            })),
            widgetData: {
              placeholder:
                I18n.Components.EventSelectModal.SelectEventTitleLowerCase,
              availableEventStatus: ['pending', 'open'],
              sortForPortalSelector: [
                'status desc',
                'event_days/start_date_time asc',
                'name asc',
              ],
              isMultiple: false,
              maxItems: 1,
              minItems: 1,
              select: EVENT_SELECTOR_SELECT,
              updateSelectOptionsWhenSingleSelected: true,
            },
            widget: WIDGET_COMPONENT.select_event_with_grid_modal,
            asyncValidators: [validator],
          },
          chargeable: {
            title: I18n.Forms.GridForm.ChargeableOnly,
            widget: WIDGET_COMPONENT.switch,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'standard-width',
            defaultValue: true,
          },
        },
      },
    },
  );
};

/** @deprecated
 * it not used anywhere
 */
const reInitializeModal = (reInitializeSites): IModalDialogOptions => {
  return merge(
    {},
    {
      title: 'Site Selector',
      validation: true,
      modalClass: `modal-lg hidden-header-checkbox`,
      buttons: [
        {
          ...BTN_DONE,
          caption: 'Re-initialize',
          disabled: (value): boolean => isEmpty(value.sites),
        },
        BTN_CANCEL,
      ],
      schema: {
        type: 'object',
        properties: {
          sites: {
            type: 'array',
          },
        },
      },
      formRules: {
        fields: {
          sites: {
            title: null,
            ignoreAutoTitleWhenTranslate: true,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.grid_list,
            widgetData: {
              gridHeight: '270px',
              hideActionPanel: true,
              valueField: 'id',
              selectOptions: reInitializeSites,
              userGridOptions: {
                isRowSelectable({ data }): boolean {
                  return data.enableStandWorksheet;
                },
                getRowClass({ data }): string {
                  return !data.enableStandWorksheet ? 'row-disabled' : '';
                },
              },
              columns: [
                {
                  title: 'Site ID',
                  path: 'store_number',
                  defaultView: true,
                  sortable: false,
                  headerComponentParams: {
                    hintOptions: {
                      description:
                        STAND_WORKSHEET_REINITIALIZED_TOOLTIP_MESSAGE,
                    },
                  },
                },
              ],
            },
          },
        },
      },
    },
  );
};

const viewSettingModal = ({
  chargeable,
  summary_view,
  can_open_drawer = false,
}: IViewSettingModalParams): IModalDialogOptions => {
  return merge(
    {},
    {
      title: I18n.Common.ViewSetting,
      validation: true,
      modalClass: `modal-md hidden-header-checkbox`,
      buttons: [BTN_DONE, BTN_CANCEL],
      value: {
        chargeable,
        summary_view,
        can_open_drawer,
      },
      schema: {
        type: 'object',
        properties: {
          summary_view: {
            type: 'boolean',
          },
          chargeable: {
            type: 'boolean',
          },
          can_open_drawer: {
            type: 'boolean',
          },
        },
      },
      formRules: {
        fields: {
          summary_view: {
            title: I18n.Forms.StandWorksheetForm.SummaryView,
            widget: WIDGET_COMPONENT.switch,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'standard-width',
          },
          chargeable: {
            title: I18n.Forms.GridForm.ChargeableOnly,
            widget: WIDGET_COMPONENT.switch,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'standard-width',
          },
          can_open_drawer: {
            title: I18n.Forms.StandWorksheetForm.ShowEntryFormPage,
            widget: WIDGET_COMPONENT.switch,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'standard-width',
          },
        },
      },
    },
  );
};

const transferSummaryModal = (
  formService: StandWorksheetFormService,
  modalTitle,
  labelDataObs,
  showLoaderObs,
  fieldType,
): IModalDialogOptions => {
  const isPreEventType = isEqual(fieldType, 'pre_event_in');
  const isDuringEventOutType = isEqual(fieldType, 'during_event_out');

  return merge({}, <IModalDialogOptions>{
    title: modalTitle,
    modalClass: `modal-hg`,
    buttons: [
      {
        ...BTN_CLOSE,
        click: formService.closeTransferSummaryDialog.bind(formService),
      },
    ],
    schema: {
      type: 'object',
      properties: {
        modal_info: { type: 'string' },
        transfers: {
          type: 'array',
        },
      },
    },
    formRules: {
      fields: {
        modal_info: {
          show: true,
          widget: WIDGET_COMPONENT.multiple_label,
          widgetFamily: WIDGET_FAMILY.XUX,
          widgetData: {
            itemClass: COMMON_TEXT_GENERAL_MESSAGE_CLASS,
            dataObs: labelDataObs,
          },
        },
        transfers: {
          title: null,
          ignoreAutoTitleWhenTranslate: true,
          widgetFamily: WIDGET_FAMILY.XUX,
          widget: WIDGET_COMPONENT.edit_grid_custom,
          widgetData: {
            gridHeight: '355px',
            hideActionPanel: true,
            valueField: '_id',
            showSearch: false,
            multiselect: false,
            orderControls: false,
            disableColumnSelector: true,
            columns: [
              {
                title: I18n.Forms.TransferForm.TransferID,
                path: 'internal_number',
                defaultView: true,
                sortable: true,
                width: 70,
                filterType: 'stringFilter',
                filterParams: {
                  quickFilter: true,
                },
                cellRendererSelector: (
                  params: ICellRendererParams,
                ): ComponentSelectorResult | null => {
                  return !params.node.rowPinned
                    ? { component: CELL_RENDERER.modal_link_render }
                    : null;
                },
                cellRendererParams: {
                  showModalDialogHandler:
                    formService.showTransferDetailModalDialogHandler.bind(
                      formService,
                    ),
                },
              },
              {
                title: I18n.Forms.TransferForm.TransactionDateTime,
                path: 'date',
                defaultView: true,
                sortable: true,
                minWidth: 165,
                maxWidth: 165,
                filterType: CELL_FILTER.dateLocale,
                filterParams: {
                  suppressUIFiltering: false,
                },
                valueFormatter: (params): string => {
                  return !params.node.rowPinned
                    ? formService.companyUtilsService.convertUTCDateWithTimeZone(
                        params.value,
                        true,
                        TIME_FORMATS.L_LT,
                      )
                    : '';
                },
                comparator: (valueA, valueB): any => {
                  if (valueA === valueB) {
                    return 0;
                  }

                  return toMoment(valueA).toISOString() >
                    toMoment(valueB).toISOString()
                    ? 1
                    : -1;
                },
              },
              {
                title: I18n.Common.Status,
                path: 'status',
                defaultView: true,
                sortable: true,
                minWidth: 90,
                maxWidth: 90,
                filterType: CELL_FILTER.enum,
                filterParams: {
                  values: isPreEventType
                    ? [
                        {
                          text: formService.i18nService.instant(
                            I18n.Status.Completed,
                          ),
                          value: TRANSFER_STATUS_VALUE.Completed,
                        },
                      ]
                    : [
                        {
                          text: formService.i18nService.instant(
                            I18n.Status.Open,
                          ),
                          value: TRANSFER_STATUS_VALUE.Open,
                        },
                        {
                          text: formService.i18nService.instant(
                            I18n.Status.Pending,
                          ),
                          value: TRANSFER_STATUS_VALUE.Pending,
                        },
                        {
                          text: formService.i18nService.instant(
                            I18n.Status.Completed,
                          ),
                          value: TRANSFER_STATUS_VALUE.Completed,
                        },
                      ],
                  suppressUIFiltering: false,
                },
                cellRenderer: (record: ICellRendererParams): string =>
                  formService.i18nService.instant(
                    TRANSFER_STATUS_NAME_OPTIONS[startCase(record?.value)],
                  ),
              },
              {
                title: isDuringEventOutType ? I18n.Common.To : I18n.Common.From,
                path: isDuringEventOutType ? 'site_name_to' : 'site_name',
                defaultView: true,
                sortable: true,
                filterType: 'stringFilter',
              },
              {
                title: I18n.Forms.TransferForm.TransferUnit,
                path: 'measure',
                defaultView: true,
                sortable: true,
                width: 100,
                filterType: 'stringFilter',
              },
              {
                title: I18n.Forms.TransferForm.Qty,
                path: 'qty',
                sortable: true,
                defaultView: true,
                width: 110,
                valueFormatter: (params): string => {
                  return !params.node.rowPinned
                    ? formService.numberFormatService.getNumberFormatter(false)(
                        params,
                      )
                    : '';
                },
                ...COLUMN_ALIGN_RIGHT,
              },
              {
                title: I18n.Forms.TransferForm.QtyInBaseUnit,
                path: 'qty_each',
                sortable: true,
                defaultView: true,
                width: 110,
                valueFormatter: (params): string => {
                  return !isNil(params.value)
                    ? formService.numberFormatService.getNumberFormatter(false)(
                        params,
                      )
                    : '';
                },
                ...COLUMN_ALIGN_RIGHT,
              },
              {
                defaultView: true,
                title: I18n.Forms.GridForm.BaseUnit,
                path: 'item_base_unit',
                width: 100,
              },
            ],
            showLoader: showLoaderObs,
            userGridOptions: {
              rowClassRules: {
                'data-pinned-bottom': ({ node }): boolean => node.rowPinned,
              },
            },
            eventHandlers: {
              [XUX_GRID_ACTIONS.GRID_READY]:
                formService.handleTransferModalListGridReady.bind(formService),
            },
          },
        },
      },
    },
  });
};

const pendingTransferSummaryModal = (options: {
  formService: StandWorksheetFormService;
  showLoaderObs: Observable<boolean>;
  subTitle: Observable<string>;
  hiddenPendingInfoObs: Observable<boolean>;
  i18nService: XVII18nService;
}): IModalDialogOptions => {
  const {
    formService,
    showLoaderObs,
    subTitle,
    hiddenPendingInfoObs,
    i18nService,
  } = options;

  return merge({}, <IModalDialogOptions>{
    title: I18n.Forms.TransferForm.PendingTransferList,
    modalClass: `modal-hg`,
    subTitleObs: subTitle,
    buttons: [
      {
        ...BTN_CLOSE,
        click: formService.closePendingTransferSummaryDialog.bind(formService),
      },
    ],
    schema: {
      type: 'object',
      properties: {
        label_info: { type: 'string' },
        transfers: {
          type: 'array',
        },
      },
    },
    formRules: {
      fields: {
        label_info: {
          widget: WIDGET_COMPONENT.label,
          widgetFamily: WIDGET_FAMILY.LEGACY,
          show: true,
          hiddenObs: hiddenPendingInfoObs,
          title: I18n.InfoMessages.DMSyncInfoMessage,
          widgetData: {
            itemClass: COMMON_WARN_MESSAGE_CLASS,
          },
        },
        transfers: {
          title: null,
          ignoreAutoTitleWhenTranslate: true,
          widgetFamily: WIDGET_FAMILY.XUX,
          widget: WIDGET_COMPONENT.edit_grid_custom,
          widgetData: {
            gridHeight: '355px',
            hideActionPanel: false,
            valueField: '_id',
            showSearch: true,
            multiselect: false,
            orderControls: false,
            disableColumnSelector: true,
            columns: [
              {
                title: I18n.Common.Date,
                path: 'transaction_date',
                defaultView: true,
                sortable: true,
                width: 100,
                filterType: CELL_FILTER.dateLocale,
                filterParams: {
                  suppressUIFiltering: false,
                },
                comparator: (valueA, valueB): any => {
                  if (valueA === valueB) {
                    return 0;
                  }

                  return toMoment(valueA).toISOString() >
                    toMoment(valueB).toISOString()
                    ? 1
                    : -1;
                },
              },
              {
                title: I18n.Forms.GridForm.ID,
                path: 'internal_number',
                defaultView: true,
                sortable: true,
                width: 70,
                filterType: 'stringFilter',
                filterParams: {
                  quickFilter: true,
                },
                cellRendererSelector: (
                  params: ICellRendererParams,
                ): ComponentSelectorResult | null => {
                  return !params.node.rowPinned
                    ? { component: CELL_RENDERER.modal_link_render }
                    : null;
                },
                cellRendererParams: {
                  showModalDialogHandler:
                    formService.showTransferDetailModalDialogHandler.bind(
                      formService,
                    ),
                  isPendingTransfer: true,
                },
              },
              {
                title: I18n.Forms.TransferForm.FromName,
                path: 'site_name',
                defaultView: true,
                sortable: true,
                filterType: 'stringFilter',
                filterParams: {
                  quickFilter: true,
                },
              },
              {
                title: I18n.Forms.TransferForm.FromID,
                path: 'store_number',
                defaultView: true,
                sortable: true,
                width: 100,
                filterType: 'stringFilter',
                filterParams: {
                  quickFilter: true,
                },
              },
              {
                title: I18n.Forms.TransferForm.ToName,
                path: 'site_name_to',
                defaultView: true,
                sortable: true,
                filterType: 'stringFilter',
                filterParams: {
                  quickFilter: true,
                },
              },
              {
                title: I18n.Forms.TransferForm.ToID,
                path: 'store_number_to',
                defaultView: true,
                sortable: true,
                width: 100,
                filterType: 'stringFilter',
                filterParams: {
                  quickFilter: true,
                },
              },
              {
                title: I18n.Common.Type,
                path: 'transfer_type',
                defaultView: true,
                sortable: true,
                width: 150,
                filterType: CELL_FILTER.enum,
                filterParams: {
                  values: [
                    {
                      text: formService.i18nService.instant(
                        I18n.Forms.TransferForm.DuringEvent,
                      ),
                      value: TRANSFER_TYPE.DURING_EVENT_TRANSFER,
                    },
                    {
                      text: formService.i18nService.instant(
                        I18n.Forms.TransferForm.OutsideofEvent,
                      ),
                      value: TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
                    },
                  ],
                  suppressUIFiltering: false,
                },
                valueFormatter: (params) =>
                  transferTypeFormatter(params, { i18nService }),
              },
              {
                path: COLUMN_SELECTOR_ID,
                width: 50,
                title: '',
                defaultView: true,
                cellRenderer: CELL_RENDERER.transfer_actions_renderer,
                cellRendererParams: {
                  redirectFunc:
                    formService.showTransferDetailModalDialogHandler.bind(
                      formService,
                    ),
                  hiddenButtons: [
                    COMMON_ACTION_TYPES.Print,
                    COMMON_ACTION_TYPES.PrintWrittenSheet,
                    COMMON_ACTION_TYPES.Export,
                    COMMON_ACTION_TYPES.Duplicate,
                  ],
                  refreshFunc:
                    formService.refreshPendingTransferList.bind(formService),
                },
                pinned: detectMobile() ? false : 'right',
                lockPinned: true,
              },
            ],
            showLoader: showLoaderObs,
            eventHandlers: {
              [XUX_GRID_ACTIONS.GRID_READY]:
                formService.handlePendingTransferModalListGridReady.bind(
                  formService,
                ),
            },
            userGridOptions: {
              rowClassRules: {
                'xvi-grid-warn-row': (params: CellClassParams): boolean =>
                  isNeedDMSync(params?.data),
              },
            },
          },
        },
      },
    },
  });
};

export const standWorkSheetModalDefinition = {
  eventSelectorModal,
  printOpenSheetsModal,
  reInitializeModal,
  viewSettingModal,
  transferSummaryModal,
  pendingTransferSummaryModal,
};
